// Copyright Open Trade Technology Ltd. 2023. All rights reserved.

import * as yup from 'yup'

import { ChainConfigName } from './DeploymentConfigs'
import { PoolTypeEnum, PoolTypeToString, yupEthAddrRequired } from './Enums'
import { EthAddr, StringTypeEnum, toFieldMetaData } from './SchemaTypes'
import { ZeroAddress } from 'ethers'

export enum ContractNameEnum {
  Pool = 'Pool',
  PoolFlex = 'PoolFlex',
  PoolDynamic = 'PoolDynamic',
  ServiceConfiguration = 'ServiceConfiguration',
  ERC20 = 'ERC20',
  Loan = 'Loan',
  Unknown = 'Unknown',
}

export const ContractCTOSchemaObjectValues = {
  contractAddr: yupEthAddrRequired
    .label('Vault Address')
    .required()
    .meta(
      toFieldMetaData({
        propName: 'contractAddr',
        description: 'The smart-contract address of the loan vault.',
        stringType: StringTypeEnum.ETH_ADDR,
      })
    ),
  serviceConfigurationAddr: yupEthAddrRequired
    .required()
    .label('Service Configuration Address')
    .meta(
      toFieldMetaData({
        propName: 'serviceConfigurationAddr',
        description: 'The smart-contract address of the service configuration.',
        stringType: StringTypeEnum.ETH_ADDR,
      })
    ),

  contractName: yup
    .mixed<ContractNameEnum>()
    .oneOf(Object.values(ContractNameEnum))
    .defined()
    .label('Contract Name')
    .meta(
      toFieldMetaData({
        propName: 'contractName',
        description: 'The name of the contract',
      })
    ),
  symbol: yup
    .string()
    .required()
    .label('symbol ')
    .meta(
      toFieldMetaData({
        propName: 'symbol',
        description: 'symbol if it is a ERC20 token',
      })
    ),
  providerUrl: yup
    .string()
    .required()
    .label('providerUrl')
    .meta(
      toFieldMetaData({
        propName: 'providerUrl',
        description: 'The URL of the provider for the contract',
      })
    ),
  backupProviderUrl: yup
    .string()
    .label('backupProviderUrl')
    .optional()
    .meta(
      toFieldMetaData({
        propName: 'backupProviderUrl',
        description: 'The URL of the backup provider for the contract',
      })
    ),
  poolType: yup
    .number<PoolTypeEnum>()
    .oneOf(Object.values(PoolTypeEnum).filter((v) => typeof v === 'number') as number[])
    .label('Pool Type')
    .required()
    .meta(
      toFieldMetaData({
        propName: 'poolType',
        description: 'Type 0 is a term pool, type 1 is a flexible pool, type 2 is a dynamic pool.',
        stateToString: PoolTypeToString,
      })
    ),

  chainConfigName: yup
    .string<ChainConfigName>()
    .required()
    .label('Chain Config Name')
    .meta(
      toFieldMetaData({
        propName: 'chainConfigName',
        description: 'The name of the blockchain network configuration',
      })
    ),
  creationBlock: yup
    .number()
    .required()
    .label('Creation Block Number')
    .meta(
      toFieldMetaData({
        propName: 'creationBlock',
        description: 'The block number when the pool was created',
      })
    ),
  creationTimestamp: yup
    .number()
    .required()
    .label('Creation Timestamp')
    .meta(
      toFieldMetaData({
        propName: 'creationTimestamp',
        description: 'The timestamp when the pool was created',
      })
    ),
  poolAddr: yupEthAddrRequired.label('Vault Address').meta(
    toFieldMetaData({
      propName: 'poolAddr',
      description: 'The smart-contract address of the loan vault. ',
      stringType: StringTypeEnum.ETH_ADDR,
    })
  ),

  name: yup
    .string()
    .required()
    .label('Vault Name')
    .meta(toFieldMetaData({ propName: 'name', description: 'Name of the Loan Vault', example: 'TokenName' })),

  liquidityAssetAddr: yupEthAddrRequired
    .label('Liquidity Asset Address')
    .required()
    .default(process.env.NODE_ENV === 'test' ? ZeroAddress : undefined)
    .meta(
      toFieldMetaData({
        propName: 'liquidityAssetAddr',
        description: 'The smart-contract address for the liquidity asset e.g. USDC, EURC. ',
        stringType: StringTypeEnum.ETH_ADDR,
      })
    ),
  liquidityTokenSymbol: yup
    .string()
    .required()
    .label('Symbol for Liquidity Token')
    .meta(
      toFieldMetaData({
        propName: 'liquidityTokenSymbol',
        description: 'Symbol for the Liquidity Token',
        example: 'USDC',
      })
    ),
  currencyLabel: yup
    .string()
    .required()
    .default(' ')
    .label('Label for Liquidity Token')
    .meta(
      toFieldMetaData({
        propName: 'currencyLabel',
        description: 'Label for the Liquidity Token',
        example: 'USDC',
      })
    ),

  //
}
export interface ContractCTO {
  contractAddr: EthAddr
  contractName: ContractNameEnum
  poolType: PoolTypeEnum
  chainConfigName: ChainConfigName
  creationBlock: number
  creationTimestamp: number
  providerUrl: string
  symbol: string
  backupProviderUrl?: string
  serviceConfigurationAddr: EthAddr
  name: string
  liquidityAssetAddr: EthAddr
  liquidityTokenSymbol: string
  currencyLabel: string
  poolAddr: EthAddr // NOTE: Redundant field ("contractAddr")
}

export interface PoolContractCTO {
  name: string
  liquidityAssetAddr: EthAddr
  liquidityTokenSymbol: string
  currencyLabel: string
  poolAddr: EthAddr
  contractName: ContractNameEnum
  poolType: PoolTypeEnum
  chainConfigName: ChainConfigName
  symbol: string
}

//ADDED
export const PoolContractCTOSchemaObjectValues = {
  name: ContractCTOSchemaObjectValues.name,
  liquidityAssetAddr: ContractCTOSchemaObjectValues.liquidityAssetAddr,
  liquidityTokenSymbol: ContractCTOSchemaObjectValues.liquidityTokenSymbol,
  currencyLabel: ContractCTOSchemaObjectValues.currencyLabel,
  poolAddr: ContractCTOSchemaObjectValues.poolAddr,
  contractName: ContractCTOSchemaObjectValues.contractName,
  poolType: ContractCTOSchemaObjectValues.poolType,
  chainConfigName: ContractCTOSchemaObjectValues.chainConfigName,
  symbol: ContractCTOSchemaObjectValues.symbol,
}

export const PoolContractCTOSchema = yup.object(PoolContractCTOSchemaObjectValues).label('PoolContractCTO')
//type PoolContractCTOInfer = yup.InferType<typeof PoolContractCTOSchema>

//Done ADDED

export const ContractCTOSchemaInfer = yup.object(ContractCTOSchemaObjectValues).label('ContractCTO')
export const ContractCTOSchema: yup.ObjectSchema<ContractCTO> = ContractCTOSchemaInfer
type ContractCTOInfer = yup.InferType<typeof ContractCTOSchemaInfer>

export const _testAPoolBaseOverview: ContractCTO = {} as unknown as ContractCTOInfer
export const _testBPoolBaseOverview: ContractCTOInfer = {} as unknown as ContractCTO
